import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { OrderStatuses } from 'entities/Order/model/consts/orderStatuses';

import { ChangeOrderStatusButton } from './ChangeOrderStatusButton';

interface PlaceOrderButtonProps {
    orderId: string;
    isNoItemsInOrder: boolean;
    isRequiredPoNumberForOrder: boolean;
    hasLoadingPnA: boolean;
    shouldShowUnavailableLabel: boolean;
    unavailableItemCount: number;
}

export const ApproveOrderButton = memo(
    ({
        orderId,
        isNoItemsInOrder,
        isRequiredPoNumberForOrder,
        hasLoadingPnA,
        shouldShowUnavailableLabel,
        unavailableItemCount,
    }: PlaceOrderButtonProps) => {
        const { t } = useTranslation();

        const submitLabel = useMemo(() => {
            if (isRequiredPoNumberForOrder) {
                return t('PO required');
            }
            if (shouldShowUnavailableLabel) {
                return unavailableItemCount === 1
                    ? t('1 item unavailable')
                    : t('{{count}} items unavailable', {
                          count: unavailableItemCount,
                      });
            }
            return t('Submit order');
        }, [
            isRequiredPoNumberForOrder,
            shouldShowUnavailableLabel,
            unavailableItemCount,
            t,
        ]);

        return (
            <ChangeOrderStatusButton
                newStatus={OrderStatuses.APPROVED}
                orderId={orderId}
                messageOnSuccess={t('Order submitted to supplier(s)')}
                variant="contained"
                disabled={
                    isNoItemsInOrder ||
                    isRequiredPoNumberForOrder ||
                    hasLoadingPnA
                }
                isRequiredPoNumberForOrder={isRequiredPoNumberForOrder}
                buttonTestClass="btn-order-submit"
                confirm={{
                    title: 'Submit order',
                    text: 'Are you sure you want to submit this order to supplier(s)?',
                    confirmLabel: 'Submit order',
                }}
            >
                {submitLabel}
            </ChangeOrderStatusButton>
        );
    },
);
